import React from 'react';
import { Button, DatePicker, Input, InputNumber, Popconfirm, Segmented, Select, Switch, Tooltip, Checkbox as AntdCheckbox } from 'antd';
import Checkbox from '@mui/material/Checkbox';
import { PositionSelect } from '../PositionSelect/PositionSelect';
import { withPermission } from './withPermission';
import { usePermissionCheck } from './usePermissionCheck';

// Simple components that can be wrapped with the HOC
export const PermissionedButton = withPermission(Button, {
  defaultTooltipMessage: "You don't have permission to perform this action"
});

export const PermissionedDatePicker = withPermission(DatePicker, {
  defaultTooltipMessage: "You don't have permission to modify this date"
});

// Export RangePicker for PermissionedDatePicker
PermissionedDatePicker.RangePicker = withPermission(DatePicker.RangePicker, {
  defaultTooltipMessage: "You don't have permission to modify this date range"
});

export const PermissionedInput = withPermission(Input, {
  defaultTooltipMessage: "You don't have permission to edit this field"
});

export const PermissionedInputNumber = withPermission(InputNumber, {
  defaultTooltipMessage: "You don't have permission to edit this number"
});

export const PermissionedSegmented = withPermission(Segmented, {
  defaultTooltipMessage: "You don't have permission to change this selection"
});

export const PermissionedSelect = withPermission(Select, {
  defaultTooltipMessage: "You don't have permission to change this selection"
});

export const PermissionedCheckboxMUI = withPermission(Checkbox, {
  defaultTooltipMessage: "You don't have permission to change this selection"
});
// Switch component with tooltip support
export const PermissionedSwitch = React.forwardRef(({ permissionCheck, permissionMessage, tooltipMessage, children, ...props }, ref) => {
  const { permissionDisabled } = usePermissionCheck(permissionCheck);
  
  const finalTooltipMessage = tooltipMessage || permissionMessage || "You don't have permission to modify this setting";

  return (
    <Tooltip title={permissionDisabled ? finalTooltipMessage : undefined}>
      <Switch
        {...props}
        ref={ref}
        disabled={permissionDisabled || props.disabled}
      />
    </Tooltip>
  );
});

// Position Select component with tooltip support
const PositionSelectWithPermission = React.forwardRef(({ permissionCheck, permissionMessage, tooltipMessage, children, ...props }, ref) => {
  const { permissionDisabled } = usePermissionCheck(permissionCheck);
  
  const finalTooltipMessage = tooltipMessage || permissionMessage || "You don't have permission to change this selection";

  return (
    <Tooltip title={permissionDisabled ? finalTooltipMessage : undefined}>
      <PositionSelect
        {...props}
        ref={ref}
        disabled={permissionDisabled || props.disabled}
      />
    </Tooltip>
  );
});

export const PermissionedPositionSelect = PositionSelectWithPermission;

// Special case for Popconfirm - needs to handle children
const PopconfirmWithPermission = ({ permissionCheck, permissionMessage, tooltipMessage, children, ...props }) => {
  const { permissionDisabled } = usePermissionCheck(permissionCheck);
  
  const finalTooltipMessage = tooltipMessage || permissionMessage || "You don't have permission to perform this action";
  
  if (permissionDisabled) {
    return (
      <Tooltip title={finalTooltipMessage}>
        <Popconfirm {...props} disabled>
          {children}
        </Popconfirm>
      </Tooltip>
    );
  }
  
  return <Popconfirm {...props}>{children}</Popconfirm>;
};

export const PermissionedPopConfirm = PopconfirmWithPermission;

// Special case for Checkbox - needs to handle children
const CheckboxWithPermission = ({ permissionCheck, permissionMessage, tooltipMessage, children, ...props }) => {
  const { permissionDisabled } = usePermissionCheck(permissionCheck);
  
  const finalTooltipMessage = tooltipMessage || permissionMessage || "You don't have permission to modify this setting";
  
  if (permissionDisabled) {
    return (
      <Tooltip title={finalTooltipMessage}>
        <AntdCheckbox {...props} disabled>
          {children}
        </AntdCheckbox>
      </Tooltip>
    );
  }
  
  return <AntdCheckbox {...props}>{children}</AntdCheckbox>;
};

export const PermissionedCheckbox = CheckboxWithPermission;

// Special case for Checkbox.Group - needs to handle children
const CheckboxGroupWithPermission = ({ permissionCheck, permissionMessage, tooltipMessage, children, ...props }) => {
  const { permissionDisabled } = usePermissionCheck(permissionCheck);
  
  const finalTooltipMessage = tooltipMessage || permissionMessage || "You don't have permission to modify these settings";
  
  if (permissionDisabled) {
    return (
      <Tooltip title={finalTooltipMessage}>
        <AntdCheckbox.Group {...props} disabled>
          {children}
        </AntdCheckbox.Group>
      </Tooltip>
    );
  }
  
  return <AntdCheckbox.Group {...props}>{children}</AntdCheckbox.Group>;
};

export const PermissionedCheckboxGroup = CheckboxGroupWithPermission;

// Export Group for PermissionedCheckbox
PermissionedCheckbox.Group = PermissionedCheckboxGroup;

// Export Option component for PermissionedSelect
PermissionedSelect.Option = Select.Option;

// Default exports
export default {
  PermissionedButton,
  PermissionedDatePicker,
  PermissionedInput,
  PermissionedInputNumber,
  PermissionedSegmented,
  PermissionedSelect,
  PermissionedPopConfirm,
  PermissionedCheckbox,
  PermissionedSwitch,
  PermissionedPositionSelect
}; 